import { useState } from 'react';
import { StatusBar, Modal, SnackbarStatus } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import styles from './GlobalItemDetailsEditModal.module.scss';
import { FieldNamesMarkedBoolean } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../hooks/QueryKeys';
import { updateGlobalItemDetails } from 'src/api/updateGlobalItemDetails';
import { formUpdateDetailsPayload } from './formUpdateDetailsPayload/formUpdateDetailsPayload';
import { GlobalItemDetailsForm } from 'src/components/CreateGlobalItemWizard/GlobalItemDetailsForm/GlobalItemDetailsForm';
import { IGlobalItem } from '../CreateGlobalItemWizard/types';
import chefPlating from 'src/assets/images/chef-plating.png';
import { FormVariant } from '../../types/menuItemForm.types';
import { hasRegulatoryStatement } from 'src/utils/regulatory-statements';
import { IMenuItemDetail, IRecipe } from 'src/types/menu.types';
import { REGULATORY_STATEMENTS, ItemType } from '../../constants';
import type { IAggregator } from 'src/types/aggregator.types';
import { getEnhancedBundleInstance, isEnhancedBundleFlagged } from 'src/utils/enhancedBundle';
import { useUpdateItemInstanceConfiguration } from 'src/hooks/useUpdateItemInstanceConfiguration/useUpdateItemInstanceConfiguration';

export interface IGlobalItemDetails {
  currentItem: IMenuItemDetail;
  selectedRecipe: IRecipe;
}

interface IGlobalItemDetailsEditModal extends Omit<IGlobalItemDetails, 'selectedRecipe'> {
  onModalClose: () => void;
  isModalOpen: boolean;
  showImageField?: boolean;
  aggregators?: IAggregator[];
}

const GlobalItemDetailsEditForm = ({
  onModalClose,
  currentItem,
  showImageField = false,
  aggregators,
}: Omit<IGlobalItemDetailsEditModal, 'isModalOpen'>) => {
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const enhancedBundleInstance = getEnhancedBundleInstance(aggregators);
  const canShowEnhancedBundleFlag = currentItem.type === ItemType.BUNDLE && !!enhancedBundleInstance;
  const isEnhancedBundle = isEnhancedBundleFlagged(
    currentItem.instance_configuration,
    enhancedBundleInstance?.instanceId,
  );
  const { mutateAsync: updateInstanceConfiguration } = useUpdateItemInstanceConfiguration(
    currentItem.itemId,
    enhancedBundleInstance?.instanceId ?? '',
  );

  const onEdit = async (
    data: Partial<IGlobalItem>,
    dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<IGlobalItem>>>,
    isDirty: boolean,
  ) => {
    clearError();
    try {
      const hasOtherChanges = isDirty && Object.keys(dirtyFields).some((key) => key !== 'is_enhanced_bundle');

      setIsSubmitting(true);

      if (hasOtherChanges) {
        const payload = formUpdateDetailsPayload(data, dirtyFields);

        await updateGlobalItemDetails(currentItem.itemId, payload);

        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.item, currentItem.itemId],
        });

        showNotification({
          status: SnackbarStatus.SUCCESS,
          message: `You have successfully updated "${currentItem.name}".`,
        });
      }

      if (dirtyFields.is_enhanced_bundle) {
        await updateInstanceConfiguration({ is_enhanced: !!data.is_enhanced_bundle });
      }

      onModalClose();
    } catch {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: 'Something went wrong whilst updating a product',
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <GlobalItemDetailsForm
        variant={FormVariant.UPDATE}
        item={{
          name: currentItem.name,
          consumer_name: currentItem.consumerName,
          recipe_id: currentItem.recipe?.product_id,
          description: currentItem.description,
          plu: currentItem.plu,
          image: currentItem.image,
          type: currentItem.type,
          is_hfss: hasRegulatoryStatement(currentItem.regulatory_statements, REGULATORY_STATEMENTS.HFSS.name),
          age_verification: hasRegulatoryStatement(
            currentItem.regulatory_statements,
            REGULATORY_STATEMENTS.AGE_VERIFICATION.name,
          ),
          party_size: currentItem.party_size,
          is_enhanced_bundle: isEnhancedBundle,
        }}
        isSubmitting={isSubmitting}
        onSaveItem={onEdit}
        onCloseOrBack={onModalClose}
        showImageField={showImageField}
        canShowEnhancedBundleFlag={canShowEnhancedBundleFlag}
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </>
  );
};

const GlobalItemDetailsEditModal = ({
  isModalOpen,
  onModalClose,
  currentItem,
  showImageField = false,
  aggregators,
}: IGlobalItemDetailsEditModal) => (
  <Modal open={isModalOpen} closeModal={onModalClose} image={chefPlating} imageAlt="chef">
    <GlobalItemDetailsEditForm
      onModalClose={onModalClose}
      currentItem={currentItem}
      showImageField={showImageField}
      aggregators={aggregators}
    />
  </Modal>
);
export default GlobalItemDetailsEditModal;
